import { motion } from "framer-motion";
import { Award, GraduationCap, Users, Lightbulb, TrendingUp, Briefcase, ShieldCheck, Sparkles, Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const TILE_PALETTES = [
  {
    // Emerald / Mint
    bg: "bg-gradient-to-br from-emerald-50 via-surface to-skylight/40",
    border: "border-emerald/20 hover:border-emerald/45",
    glow: "group-hover:shadow-emerald/25",
    iconSoft: "bg-emerald text-white",
    title: "text-ink",
    topLine: "from-emerald via-emerald/60 to-teal-400",
    chip: "text-emerald",
    chipBg: "bg-emeraldlight/60 border-emerald/30",
    blob: "from-emerald-400/30 to-teal-400/20",
  },
  {
    // Sky / Indigo
    bg: "bg-gradient-to-br from-sky-50 via-surface to-violet/15",
    border: "border-sky/25 hover:border-sky/50",
    glow: "group-hover:shadow-sky/25",
    iconSoft: "bg-sky text-white",
    title: "text-ink",
    topLine: "from-sky via-indigo-500 to-violet",
    chip: "text-sky",
    chipBg: "bg-skylight/70 border-sky/30",
    blob: "from-sky-400/30 to-indigo-400/20",
  },
  {
    // Rose / Blush
    bg: "bg-gradient-to-br from-rose-50 via-surface to-roselight/50",
    border: "border-rose/25 hover:border-rose/50",
    glow: "group-hover:shadow-rose/25",
    iconSoft: "bg-rose text-white",
    title: "text-ink",
    topLine: "from-rose via-pink-500 to-fuchsia-500",
    chip: "text-rose",
    chipBg: "bg-roselight/70 border-rose/30",
    blob: "from-rose-400/30 to-pink-400/20",
  },
  {
    // Terracotta / Amber
    bg: "bg-gradient-to-br from-amber-50 via-surface to-lemonlight/40",
    border: "border-terracotta/25 hover:border-terracotta/55",
    glow: "group-hover:shadow-terracotta/30",
    iconSoft: "bg-terracotta text-white",
    title: "text-ink",
    topLine: "from-terracotta via-amber-500 to-orange-400",
    chip: "text-terracotta",
    chipBg: "bg-amber-100 border-terracotta/35",
    blob: "from-terracotta-400/30 to-amber-400/20",
  },
  {
    // Lemon / Warm sand
    bg: "bg-gradient-to-br from-lemon/60 via-surface to-emeraldlight/40",
    border: "border-amber-400/30 hover:border-amber-500/60",
    glow: "group-hover:shadow-amber-400/25",
    iconSoft: "bg-amber-500 text-white",
    title: "text-ink",
    topLine: "from-lemon via-amber-500 to-terracotta/80",
    chip: "text-amber-700",
    chipBg: "bg-lemon/60 border-amber-400/40",
    blob: "from-amber-400/30 to-lemon-300/30",
  },
  {
    // Violet / Deep
    bg: "bg-gradient-to-br from-violet/10 via-surface to-sky-50",
    border: "border-violet/30 hover:border-violet/55",
    glow: "group-hover:shadow-violet/30",
    iconSoft: "bg-violet text-white",
    title: "text-ink",
    topLine: "from-violet via-purple-500 to-indigo-500",
    chip: "text-violet",
    chipBg: "bg-violet/10 border-violet/35",
    blob: "from-violet-400/30 to-purple-400/20",
  },
];

const ICONS = {
  certificate: Award,
  project: GraduationCap,
  mentor: Users,
  idea: Lightbulb,
  monetize: TrendingUp,
  job: Briefcase,
  verified: ShieldCheck,
};

export default function BenefitsGrid({ benefits, testId = "benefits-grid" }) {
  if (!benefits || benefits.length === 0) return null;
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 auto-rows-fr"
    >
      {benefits.map((b, i) => {
        const Icon = ICONS[b.icon] || Sparkles;
        const t = TILE_PALETTES[i % TILE_PALETTES.length];
        return (
          <Reveal key={b.title} delay={(i % 3) * 0.06}>
            <motion.article
              initial={{ opacity: 0, y: 22, filter: "blur(6px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-40px" }}
              whileHover={{ y: -10, scale: 1.015 }}
              transition={{
                opacity: { duration: 0.6, delay: 0.02 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
                y: { duration: 0.6, delay: 0.02 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
                filter: { duration: 0.6, delay: 0.02 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
                scale: { type: "spring", stiffness: 220, damping: 20, mass: 0.9 },
              }}
              className={`group relative h-full rounded-[1.75rem] p-[1px] shadow-md hover:shadow-2xl transition-shadow duration-500 ${t.glow} ${t.border} ${t.bg} border overflow-hidden`}
            >
              {/* Colored blob corner */}
              <motion.div
                aria-hidden="true"
                className={`pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br ${t.blob} opacity-80 blur-2xl`}
                initial={{ scale: 0.6, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 0.9 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.3 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-20 -left-10 w-60 h-60 rounded-full bg-white/55 blur-3xl"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 0.9 }}
                transition={{ duration: 0.7 }}
              />

              {/* Top accent animated line */}
              <div
                aria-hidden="true"
                className={`absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r ${t.topLine} opacity-90`}
              />
              <motion.div
                aria-hidden="true"
                className={`absolute top-0 left-0 h-[2.5px] w-1/2 bg-gradient-to-r from-white/90 via-white/60 to-transparent`}
                initial={{ x: "-150%" }}
                whileHover={{ x: "200%" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              />

              <div className="relative rounded-[calc(1.75rem-1px)] bg-white/40 backdrop-blur-[2px] p-6 md:p-7 h-full flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <motion.span
                      className={`flex w-12 h-12 rounded-2xl items-center justify-center shadow-md ${t.iconSoft}`}
                      whileHover={{ scale: 1.12, rotate: -8 }}
                      transition={{ type: "spring", stiffness: 400, damping: 12 }}
                    >
                      <Icon size={22} />
                    </motion.span>
                    <motion.span
                      aria-hidden="true"
                      className="w-2 h-2 rounded-full bg-terracotta"
                      animate={{ y: [0, -2, 0], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.25 }}
                    />
                  </div>
                  <motion.span
                    className="font-display font-black text-[22px] text-ink/15 leading-none pt-1 select-none tabular-nums"
                    initial={{ opacity: 0, x: -6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.35 + i * 0.05 }}
                  >
                    0{i + 1}
                  </motion.span>
                </div>

                <motion.h4
                  className={`mt-5 font-display font-bold text-lg md:text-[19px] leading-snug ${t.title} min-h-[3.5rem]`}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }}
                >
                  {b.title}
                </motion.h4>

                <motion.p
                  className="mt-3 text-[14.5px] leading-relaxed text-graphite flex-1"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.25 + i * 0.05 }}
                >
                  {b.desc}
                </motion.p>

                <div className="mt-6 pt-4 border-t border-line/70 flex items-center justify-between">
                  <motion.span
                    className={`inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full border px-2.5 py-1 ${t.chipBg} ${t.chip}`}
                    whileHover={{ scale: 1.05 }}
                  >
                    <Check size={13} />
                    Included
                  </motion.span>
                  <motion.span
                    className={`inline-flex items-center gap-1 text-[12px] font-bold ${t.chip} opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all duration-300`}
                  >
                    Benefit <ArrowUpRight size={13} />
                  </motion.span>
                </div>
              </div>
            </motion.article>
          </Reveal>
        );
      })}
    </div>
  );
}
